import React, { useEffect, useState } from 'react';
import {
  Card,
  List,
  Button,
  Modal,
  message,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Empty,
  Radio,
  Badge,
  // Popconfirm, // Not needed - delete not supported
} from 'antd';
import {
  BellOutlined,
  CheckOutlined,
  // DeleteOutlined, // Not needed - delete not supported
  ReloadOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchNotifications,
  fetchNotificationDetail,
  markAsRead,
  markAllAsRead,
  // deleteNotification, // Backend doesn't support DELETE notification
  fetchUnreadCount,
} from '../store/slices/notificationSlice';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-cn';

dayjs.extend(relativeTime);
dayjs.locale('zh-cn');

const { Title, Text, Paragraph } = Typography;

const NotificationsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterIsRead, setFilterIsRead] = useState<boolean | undefined>(undefined);

  const {
    notifications,
    currentNotification,
    unreadCount,
    markingAsRead,
    error,
  } = useAppSelector((state) => state.notification);

  useEffect(() => {
    dispatch(fetchNotifications({ page: 1, limit: 20 }));
    dispatch(fetchUnreadCount());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  const handleViewDetail = async (id: string, isRead: boolean) => {
    try {
      await dispatch(fetchNotificationDetail(id)).unwrap();
      setDetailModalVisible(true);
      if (!isRead) {
        await dispatch(markAsRead(id)).unwrap();
        dispatch(fetchUnreadCount());
      }
    } catch (err: any) {
      message.error(err || '获取通知详情失败');
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await dispatch(markAsRead(id)).unwrap();
      message.success('已标记为已读');
      dispatch(fetchUnreadCount());
    } catch (err: any) {
      message.error(err || '标记失败');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await dispatch(markAllAsRead()).unwrap();
      message.success('已标记全部为已读');
      dispatch(fetchUnreadCount());
    } catch (err: any) {
      message.error(err || '标记失败');
    }
  };

  // Note: Backend doesn't support DELETE notification endpoint
  // const handleDelete = async (id: string) => {
  //   try {
  //     await dispatch(deleteNotification(id)).unwrap();
  //     message.success('通知已删除');
  //     dispatch(fetchUnreadCount());
  //   } catch (err: any) {
  //     message.error(err || '删除失败');
  //   }
  // };

  const handleRefresh = () => {
    const filters: any = {};
    if (filterIsRead !== undefined) {
      filters.is_read = filterIsRead;
    }
    if (filterType !== 'all') {
      filters.type = filterType;
    }
    dispatch(fetchNotifications({ page: 1, limit: 20, filters }));
    dispatch(fetchUnreadCount());
  };

  const handleFilterChange = (isRead: boolean | undefined, type: string) => {
    setFilterIsRead(isRead);
    setFilterType(type);
    const filters: any = {};
    if (isRead !== undefined) {
      filters.is_read = isRead;
    }
    if (type !== 'all') {
      filters.type = type;
    }
    dispatch(fetchNotifications({ page: 1, limit: 20, filters }));
  };

  const getNotificationIcon = (type: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      info: <InfoCircleOutlined style={{ color: '#2F6BFF' }} />,
      success: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      warning: <WarningOutlined style={{ color: '#faad14' }} />,
      error: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
    };
    return iconMap[type] || <BellOutlined />;
  };

  const getNotificationTypeTag = (type: string) => {
    const typeMap: Record<string, { label: string; color: string }> = {
      info: { label: '通知', color: 'blue' },
      success: { label: '成功', color: 'green' },
      warning: { label: '警告', color: 'orange' },
      error: { label: '错误', color: 'red' },
    };
    const config = typeMap[type] || { label: type, color: 'default' };
    return <Tag color={config.color}>{config.label}</Tag>;
  };

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          通知中心
        </Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={handleMarkAllAsRead}
            loading={markingAsRead}
            disabled={unreadCount === 0}
          >
            标记全部已读
          </Button>
        </Space>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="总通知数"
              value={notifications.meta?.total || 0}
              prefix={<BellOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="未读通知"
              value={unreadCount}
              valueStyle={{ color: '#faad14' }}
              prefix={<Badge status="processing" />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="已读通知"
              value={(notifications.meta?.total || 0) - unreadCount}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Space
          style={{
            width: '100%',
            justifyContent: 'space-between',
            marginBottom: 16,
          }}
        >
          <Space>
            <Text>状态：</Text>
            <Radio.Group
              value={filterIsRead}
              onChange={(e) => handleFilterChange(e.target.value, filterType)}
            >
              <Radio.Button value={undefined}>全部</Radio.Button>
              <Radio.Button value={false}>未读</Radio.Button>
              <Radio.Button value={true}>已读</Radio.Button>
            </Radio.Group>
          </Space>

          <Space>
            <Text>类型：</Text>
            <Radio.Group
              value={filterType}
              onChange={(e) => handleFilterChange(filterIsRead, e.target.value)}
            >
              <Radio.Button value="all">全部</Radio.Button>
              <Radio.Button value="info">通知</Radio.Button>
              <Radio.Button value="success">成功</Radio.Button>
              <Radio.Button value="warning">警告</Radio.Button>
              <Radio.Button value="error">错误</Radio.Button>
            </Radio.Group>
          </Space>
        </Space>

        <List
          loading={notifications.loading}
          dataSource={notifications.data || []}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="暂无通知"
              />
            ),
          }}
          pagination={{
            total: notifications.meta?.total || 0,
            current: notifications.meta?.current_page || 1,
            pageSize: notifications.meta?.per_page || 20,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              const filters: any = {};
              if (filterIsRead !== undefined) {
                filters.is_read = filterIsRead;
              }
              if (filterType !== 'all') {
                filters.type = filterType;
              }
              dispatch(fetchNotifications({ page, limit: pageSize, filters }));
            },
          }}
          renderItem={(item) => (
            <List.Item
              key={item.id}
              style={{
                background: item.is_read ? '#ffffff' : '#f6ffed',
                cursor: 'pointer',
                transition: 'background 0.3s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#fafafa';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = item.is_read
                  ? '#ffffff'
                  : '#f6ffed';
              }}
              onClick={() => handleViewDetail(item.id, item.is_read)}
              actions={[
                !item.is_read && (
                  <Button
                    type="link"
                    size="small"
                    icon={<CheckOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMarkAsRead(item.id);
                    }}
                  >
                    标记已读
                  </Button>
                ),
                // Backend doesn't support DELETE notification
                // <Popconfirm
                //   title="确认删除这条通知？"
                //   onConfirm={(e) => {
                //     e?.stopPropagation();
                //     handleDelete(item.id);
                //   }}
                //   onCancel={(e) => e?.stopPropagation()}
                //   okText="确认"
                //   cancelText="取消"
                // >
                //   <Button
                //     type="link"
                //     size="small"
                //     danger
                //     icon={<DeleteOutlined />}
                //     onClick={(e) => e.stopPropagation()}
                //   >
                //     删除
                //   </Button>
                // </Popconfirm>,
              ].filter(Boolean)}
            >
              <List.Item.Meta
                avatar={
                  <div style={{ fontSize: 24 }}>
                    {getNotificationIcon(item.type)}
                  </div>
                }
                title={
                  <Space>
                    {!item.is_read && (
                      <Badge status="processing" style={{ marginRight: 4 }} />
                    )}
                    <Text strong={!item.is_read}>{item.title}</Text>
                    {getNotificationTypeTag(item.type)}
                  </Space>
                }
                description={
                  <Space direction="vertical" size={4}>
                    <Paragraph
                      ellipsis={{ rows: 2 }}
                      style={{ marginBottom: 0, color: '#666' }}
                    >
                      {item.message}
                    </Paragraph>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(item.created_at).fromNow()} ·{' '}
                      {dayjs(item.created_at).format('YYYY-MM-DD HH:mm:ss')}
                    </Text>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Card>

      {/* Notification Detail Modal */}
      <Modal
        title="通知详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
        ]}
        width={700}
      >
        {currentNotification && (
          <div>
            <Space
              direction="vertical"
              size="middle"
              style={{ width: '100%' }}
            >
              <div>
                <Space>
                  {getNotificationIcon(currentNotification.type)}
                  <Title level={4} style={{ margin: 0 }}>
                    {currentNotification.title}
                  </Title>
                  {getNotificationTypeTag(currentNotification.type)}
                  {currentNotification.is_read ? (
                    <Tag color="default">已读</Tag>
                  ) : (
                    <Tag color="orange">未读</Tag>
                  )}
                </Space>
              </div>

              <Card size="small" style={{ background: '#fafafa' }}>
                <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                  {currentNotification.message}
                </Paragraph>
              </Card>

              {currentNotification.data &&
                Object.keys(currentNotification.data).length > 0 && (
                  <Card size="small" title="附加数据">
                    <pre style={{ margin: 0, background: '#f5f5f5', padding: 12 }}>
                      {JSON.stringify(currentNotification.data, null, 2)}
                    </pre>
                  </Card>
                )}

              <Space direction="vertical" size={4}>
                <div>
                  <Text type="secondary">通知ID：</Text>
                  <Text copyable>{currentNotification.id}</Text>
                </div>
                <div>
                  <Text type="secondary">创建时间：</Text>
                  <Text>
                    {dayjs(currentNotification.created_at).format(
                      'YYYY-MM-DD HH:mm:ss'
                    )}{' '}
                    ({dayjs(currentNotification.created_at).fromNow()})
                  </Text>
                </div>
                {currentNotification.read_at && (
                  <div>
                    <Text type="secondary">已读时间：</Text>
                    <Text>
                      {dayjs(currentNotification.read_at).format(
                        'YYYY-MM-DD HH:mm:ss'
                      )}{' '}
                      ({dayjs(currentNotification.read_at).fromNow()})
                    </Text>
                  </div>
                )}
              </Space>
            </Space>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default NotificationsPage;
